import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { verificationsApi } from "@/api/verifications";
import CheckELogo from "@/components/CheckELogo";
import { isValidBadgeSlug } from "@/lib/badgeSlug";

/**
 * PublicBadgePage — /verified/:badgeSlug
 *
 * Renders the "Xpert Verified" badge as a standalone, public, shareable
 * page. No auth required. Every load increments view_count server-side.
 *
 * v2 palette: paper canvas (#FAF8F4), warm ink (#0E1410), brand-green
 * accents (#17803D), green-check brand mark. This is the page people
 * will share on LinkedIn / GitHub READMEs — visual weight matters.
 *
 * Slug validation (P1-7) lives in src/lib/badgeSlug.js so it's testable
 * without pulling in React/router.
 */

export default function PublicBadgePage() {
  const { badgeSlug } = useParams();
  const [badge, setBadge]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    // Defence-in-depth: reject anything that doesn't match the slug
    // shape BEFORE we make a network request (P1-7).
    if (!isValidBadgeSlug(badgeSlug)) {
      setError("This badge doesn't exist or has been hidden.");
      setLoading(false);
      return () => { mounted = false; };
    }

    verificationsApi.getPublicBadge(badgeSlug)
      .then((data) => { if (mounted) setBadge(data); })
      .catch((err) => {
        if (!mounted) return;
        // Badges are private by default (#45): the API answers 404 for a
        // private badge exactly as for a missing one, so say both.
        setError(err?.response?.status === 404
          ? "This badge doesn't exist, or its owner kept the review private."
          : "Couldn't load this verification.");
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [badgeSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !badge) {
    return (
      <div className="min-h-screen bg-canvas text-ink font-body
                      flex flex-col items-center justify-center px-4 text-center">
        <CheckELogo size={56} className="mb-6" />
        <div className="font-display text-2xl text-ink mb-3">Badge not found</div>
        <div className="text-slateline-600 max-w-md">
          {error || "Try a different verification link."}
        </div>
        <Link
          to="/"
          className="mt-8 text-brand-green hover:underline text-sm tracking-wide"
        >
          ← Back to xpert.
        </Link>
      </div>
    );
  }

  const issued = badge.issued_at
    ? new Date(badge.issued_at).toLocaleDateString(undefined, {
        year:  "numeric",
        month: "long",
        day:   "numeric",
      })
    : "Recently";

  const confidenceColor =
    badge.confidence_level === "high"   ? "text-brand-green" :
    badge.confidence_level === "medium" ? "text-amber-600"   :
    badge.confidence_level === "low"    ? "text-rose-600"    :
    "text-slateline-500";

  // A "low" confidence verdict means the reviewer found material issues
  // ("don't act on this without changes", ExpertReviewPage). Publishing it is
  // allowed with the buyer's agreement, but it must not read as a sign-off.
  const signedOff = badge.confidence_level !== "low";

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      {/* Slim header */}
      <header className="border-b border-slateline-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <CheckELogo size={28} withWordmark />
          </Link>
          <div className="text-[11px] text-slateline-400 tracking-[0.18em] uppercase font-semibold">
            Public Verification
          </div>
        </div>
      </header>

      {/* Badge card */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div
          className="bg-white border border-slateline-200 rounded-2xl overflow-hidden
                     shadow-[0_24px_80px_-30px_rgba(15,34,64,0.18)]"
        >
          {/* Top ribbon */}
          <div className="bg-ink text-white px-6 sm:px-10 py-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-brand-green font-semibold
                            tracking-[0.18em] uppercase text-xs">
              <CheckELogo size={20} />
              {signedOff ? "Xpert Verified" : "Xpert Reviewed"}
            </div>
            <div className="text-[11px] text-white/60 tracking-wider font-mono">
              {badge.badge_slug}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 sm:px-10 py-10 sm:py-14">
            <div className="text-xs text-brand-green font-bold tracking-[0.18em] uppercase mb-3">
              {badge.category_label || badge.category}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-ink leading-snug">
              {signedOff ? "Reviewed and signed off by" : "Reviewed by"}{" "}
              <span className="text-brand-green">{badge.expert_name}</span>.
            </h1>
            {!signedOff && (
              <p className="mt-3 text-sm text-slateline-600 border-l-[3px] border-slateline-300 pl-3">
                Not a sign-off: the reviewer found material issues to fix before
                relying on this work.
              </p>
            )}
            {badge.expert_title && (
              <p className="text-slateline-600 mt-2 text-base sm:text-lg">{badge.expert_title}</p>
            )}
            {badge.expert_credentials && (
              <p className="text-slateline-500 text-sm mt-1">{badge.expert_credentials}</p>
            )}

            {/* Verdict summary */}
            {badge.verdict_summary && (
              <div className="mt-8 bg-canvas border border-slateline-200 rounded-lg p-5 sm:p-6">
                <div className="text-[11px] text-slateline-500 tracking-wider uppercase font-semibold mb-2">
                  Expert verdict
                </div>
                <p className="text-ink leading-relaxed text-base sm:text-lg">
                  {badge.verdict_summary}
                </p>
                {badge.confidence_level && (
                  <div className={`mt-4 text-xs font-bold tracking-wider uppercase ${confidenceColor}`}>
                    Confidence: {badge.confidence_level}
                  </div>
                )}
              </div>
            )}

            {/* Footer row */}
            <div className="mt-10 pt-6 border-t border-slateline-200
                            flex flex-col sm:flex-row sm:items-center
                            sm:justify-between gap-3 text-sm text-slateline-500">
              <div>Issued {issued}</div>
              <div>
                {(badge.view_count?.toLocaleString?.() || 0)} views
              </div>
            </div>
          </div>
        </div>

        {/* CTA strip */}
        <div className="mt-10 text-center">
          <div className="text-slateline-600 text-sm mb-4 italic">
            AI gets you 80% there. We close the gap.
          </div>
          <Link
            to="/get-verified"
            className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-greenHover
                       text-white font-semibold px-6 py-3 rounded-md text-sm
                       tracking-wide shadow-sm transition-colors"
          >
            Get something verified
            <span>→</span>
          </Link>
        </div>
      </main>
    </div>
  );
}
